export const REFERRAL_CHILD_ROW_MARKER = 'isReferralChildRow';
export const REFERRAL_PRIMARY_HAS_PAGE_CHILDREN_MARKER = 'hasReferralChildrenOnPage';

const isLinkedReferralRow = (row) => (
  row?.referral_entity_slug && row.is_primary_referral_record === false
);

const referralRecordCount = (row) => Number(row?.referral_entity_record_count || 0);

// A primary referral row is expandable when either:
//   (a) the server-cached `referral_entity_record_count` reports more than one
//       record in the group, or
//   (b) `groupReferralRows` saw at least one linked child for the same slug on
//       this page (annotated as `hasReferralChildrenOnPage`).
//
// (b) is necessary because the cached count on the primary can briefly lag the
// real record set (e.g. immediately after a linked record is created), and
// without it the caret would not render and the child rows below would be
// permanently hidden with no UI affordance to surface them.
export const isExpandableReferralPrimaryRow = (row) => (
  Boolean(row?.referral_entity_slug)
    && row.is_primary_referral_record === true
    && (referralRecordCount(row) > 1 || row[REFERRAL_PRIMARY_HAS_PAGE_CHILDREN_MARKER] === true)
);

const buildChildRow = (row, primaryRow) => ({
  ...row,
  [REFERRAL_CHILD_ROW_MARKER]: true,
  referralParentFileToken: primaryRow.file_token,
});

const annotatePrimaryWithPageChildren = (primaryRow) => ({
  ...primaryRow,
  [REFERRAL_PRIMARY_HAS_PAGE_CHILDREN_MARKER]: true,
});

// Shared empty Set so callers that pass `undefined`/falsy still hit the memo
// below on the next call with the same `rows` reference.
export const EMPTY_EXPANDED_REFERRAL_SLUGS = Object.freeze(new Set());

// ENG-2514: the grouped row set is read from two places per render (the page
// component for the action bar, and `RecordsHOC.transformRows` for the table
// and viewer navigation). Memoizing the most recent call keeps both paths on
// the same array reference, which also lets `RecordsHOC.componentDidUpdate`
// short-circuit identity-equal renders.
let lastMemoInputs = null;
let lastMemoResult = null;

const groupReferralRowsUncached = (rows, expandedReferralEntitySlugs) => {
  const rowsByReferralSlug = {};
  rows.forEach((row) => {
    const slug = row?.referral_entity_slug;
    if (!slug) return;

    if (!rowsByReferralSlug[slug]) rowsByReferralSlug[slug] = [];
    rowsByReferralSlug[slug].push(row);
  });

  const primaryRowsByReferralSlug = {};
  const slugsWithPageChildren = new Set();
  Object.keys(rowsByReferralSlug).forEach((slug) => {
    const rowsForSlug = rowsByReferralSlug[slug];
    const primaryRow = rowsForSlug.find((row) => row.is_primary_referral_record === true);
    if (!primaryRow) return;

    primaryRowsByReferralSlug[slug] = primaryRow;
    if (rowsForSlug.some((row) => row !== primaryRow)) {
      slugsWithPageChildren.add(slug);
    }
  });

  return rows.reduce((groupedRows, row) => {
    const slug = row?.referral_entity_slug;
    const primaryRow = slug ? primaryRowsByReferralSlug[slug] : null;

    if (primaryRow && isLinkedReferralRow(row)) {
      return groupedRows;
    }

    if (!primaryRow && isLinkedReferralRow(row)) {
      // Backend pagination slices individual records, so a linked child can
      // appear on a page without its primary. Render it as a flat top-level
      // row — the indent/grey/status-strip treatment would mislead the user
      // without a primary above it to link to. Longer term, referral inbox
      // pagination should page by referral group.
      groupedRows.push(row);
      return groupedRows;
    }

    const isPrimaryRow = primaryRow && primaryRow === row;
    const hasPageChildren = isPrimaryRow && slug && slugsWithPageChildren.has(slug);
    const visibleRow = hasPageChildren ? annotatePrimaryWithPageChildren(row) : row;

    groupedRows.push(visibleRow);

    if (!isPrimaryRow || !expandedReferralEntitySlugs.has(slug)) {
      return groupedRows;
    }

    rowsByReferralSlug[slug]
      .filter((linkedRow) => linkedRow !== primaryRow)
      .forEach((linkedRow) => groupedRows.push(buildChildRow(linkedRow, primaryRow)));

    return groupedRows;
  }, []);
};

export const groupReferralRows = (rows = [], expandedReferralEntitySlugs = EMPTY_EXPANDED_REFERRAL_SLUGS) => {
  if (
    lastMemoInputs
    && lastMemoInputs.rows === rows
    && lastMemoInputs.expandedReferralEntitySlugs === expandedReferralEntitySlugs
  ) {
    return lastMemoResult;
  }
  const result = groupReferralRowsUncached(rows, expandedReferralEntitySlugs);
  lastMemoInputs = { rows, expandedReferralEntitySlugs };
  lastMemoResult = result;
  return result;
};
